import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check, MapPin, Home, FileSignature } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const WILL_ADDONS = [
  {
    id: "notarisation",
    name: "Will Notarisation",
    price: "₹1,499",
    amount_paise: 149900,
    description: "We facilitate notarisation at your nearest notary in Mumbai.",
    note: "Available in Mumbai only — more cities coming soon.",
    icon: FileSignature,
  },
  {
    id: "notarisation_at_home",
    name: "Will Notarisation at Home",
    price: "₹2,499",
    amount_paise: 249900,
    description: "A notary visits your home in Mumbai at a time that suits you.",
    note: "Available in Mumbai only — more cities coming soon.",
    icon: Home,
  },
  {
    id: "registration",
    name: "Will Registration",
    price: "₹9,999",
    amount_paise: 999900,
    description:
      "We facilitate registration of your Will at the local Sub-Registrar office in Mumbai. Notarisation is not required if you choose registration.",
    note: "Available in Mumbai only — more cities coming soon.",
    icon: MapPin,
  },
];

/**
 * Post-payment upsell shown immediately after a successful Essential Will payment.
 * Lets the user opt-in for notarisation or registration. Selection is recorded
 * against the will via POST /api/wills/{will_id}/addon — actual fulfilment is
 * coordinated offline by the WillSeva team (lead alert email is dispatched).
 */
export default function PostPaymentAddons({ open, willId, onClose }) {
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleAdd = async () => {
    if (!selected || !willId) return;
    const addon = WILL_ADDONS.find((a) => a.id === selected);
    if (!addon) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/wills/${willId}/addon`, {
        will_id: willId,
        addon_id: addon.id,
        addon_name: addon.name,
        amount_paise: addon.amount_paise,
      });
      toast.success(
        `${addon.name} added to your order. Our team will reach out shortly to coordinate.`
      );
      onClose?.({ addon });
    } catch (e) {
      toast.error(
        e?.response?.data?.detail ||
          "Could not save your selection. Please request a callback and we'll help."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkip = () => {
    if (submitting) return;
    onClose?.({ addon: null });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        // Only allow close via explicit buttons, never via ESC/backdrop —
        // otherwise users can dismiss without our knowing the choice.
        if (!v && !submitting) handleSkip();
      }}
    >
      <DialogContent
        className="sm:max-w-xl max-h-[90vh] overflow-y-auto"
        data-testid="addon-dialog"
      >
        <DialogHeader>
          <DialogTitle
            className="font-serif text-2xl text-navy"
            data-testid="addon-dialog-title"
          >
            One Last Step — Protect Your Will Further
          </DialogTitle>
          <DialogDescription
            className="text-sm text-[#4A5568]"
            data-testid="addon-dialog-body"
          >
            A Will is valid without notarisation or registration — but adding
            one gives it an extra layer of legal protection. Choose an option
            below or skip for now.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 space-y-3" role="radiogroup" aria-label="Will add-ons">
          {WILL_ADDONS.map((a) => {
            const isSelected = selected === a.id;
            const Icon = a.icon;
            return (
              <button
                key={a.id}
                type="button"
                role="radio"
                aria-checked={isSelected}
                onClick={() =>
                  setSelected((prev) => (prev === a.id ? null : a.id))
                }
                className={`relative w-full rounded-md border p-4 text-left transition-all ${
                  isSelected
                    ? "border-gold/70 bg-[#FBF7EE] shadow-[0_8px_24px_-14px_rgba(197,160,89,0.45)]"
                    : "border-[#0A1128]/10 bg-white hover:border-gold/40 hover:bg-[#FBFAF6]"
                }`}
                data-testid={`addon-option-${a.id}`}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${
                      isSelected
                        ? "bg-gold text-navy"
                        : "bg-navy text-gold"
                    }`}
                  >
                    {isSelected ? <Check size={16} /> : <Icon size={16} />}
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <div className="font-serif text-base text-navy">
                        {a.name}
                      </div>
                      <div className="font-serif text-lg text-navy">{a.price}</div>
                    </div>
                    <p className="mt-1 text-sm text-[#4A5568]">{a.description}</p>
                    <p className="mt-2 text-xs italic text-[#4A5568]/80">
                      {a.note}
                    </p>
                    {a.id === "registration" && isSelected && (
                      <p
                        className="mt-2 rounded-sm border border-gold/30 bg-gold/10 px-2 py-1 text-xs text-navy"
                        data-testid="addon-registration-note"
                      >
                        Notarisation is not required if you register your Will.
                      </p>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-5 flex flex-col gap-2">
          <Button
            size="lg"
            onClick={handleAdd}
            disabled={!selected || submitting}
            className="w-full bg-gold text-navy hover:bg-[#E5C87A] disabled:cursor-not-allowed disabled:opacity-60"
            data-testid="addon-add-btn"
          >
            {submitting ? "Saving…" : "Add to my order"}
          </Button>
          <button
            type="button"
            onClick={handleSkip}
            disabled={submitting}
            className="text-center text-sm text-[#4A5568] underline-offset-2 hover:text-navy hover:underline disabled:cursor-not-allowed disabled:opacity-60"
            data-testid="addon-skip-btn"
          >
            No thanks, I'm done
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
